import { useEffect, useState } from 'react';
import { CONGRATS_SEEN_EVENT, readCongratsSeen } from '../shared/utils/feed-congrats-seen';

/**
 * Ç7: üst bar "yeni tebrik" rozeti. Son akış ziyaretinden (bu tarayıcıdaki `congratsSeen` kaydı) bu
 * yana kartlarına gelen tebrik sayısı. Başlık ve mobil menü aynı durumu paylaşır; istek oturum başına
 * en çok 10 dakikada bir (sessionStorage önbelleği), uygulama öne gelince tazelenir. Akış görülünce
 * (`CONGRATS_SEEN_EVENT`) sıfırlanır. Kayıt yoksa (ilk ziyaret) istek atılmaz.
 */
const CACHE_KEY = 'tenis.feed.congratsBadge';
const TTL_MS = 10 * 60 * 1000;

type Cache = { viewer: string; since: number; count: number; at: number };
let state: Cache | null = null;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(listener => listener());

const readCache = (): Cache | null => {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(CACHE_KEY) || 'null');
    return value && typeof value.count === 'number' ? value : null;
  } catch {
    return null;
  }
};
const writeCache = (value: Cache) => {
  state = value;
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    // Depolama kapalı: bellekte kalır.
  }
  emit();
};

async function refresh(viewerId: string, force = false): Promise<void> {
  const since = readCongratsSeen(viewerId)?.at;
  if (!since) return;
  const cached = state || readCache();
  if (
    !force &&
    cached &&
    cached.viewer === viewerId &&
    cached.since === since &&
    Date.now() - cached.at < TTL_MS
  ) {
    if (state !== cached) {
      state = cached;
      emit();
    }
    return;
  }
  if (inflight) return;
  inflight = (async () => {
    try {
      const { auth } = await import('./firebaseAuth');
      if (!auth.currentUser) await auth.authStateReady();
      const user = auth.currentUser;
      if (!user) return;
      const response = await fetch(`/api/feed-congrats-badge?since=${since}`, {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${await user.getIdToken()}` },
      });
      if (!response.ok) return;
      const { count } = await response.json();
      writeCache({ viewer: viewerId, since, count: Number(count) || 0, at: Date.now() });
    } catch {
      // Sessiz: rozet yalnız ipucu.
    } finally {
      inflight = null;
    }
  })();
  await inflight;
}

export function useCongratsBadge(viewerId?: string | null): number {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!viewerId) return undefined;
    const listener = () => setTick(value => value + 1);
    listeners.add(listener);
    refresh(viewerId);
    const onSeen = () => {
      writeCache({
        viewer: viewerId,
        since: readCongratsSeen(viewerId)?.at || Date.now(),
        count: 0,
        at: Date.now(),
      });
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh(viewerId);
    };
    window.addEventListener(CONGRATS_SEEN_EVENT, onSeen);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      listeners.delete(listener);
      window.removeEventListener(CONGRATS_SEEN_EVENT, onSeen);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [viewerId]);
  return viewerId && state?.viewer === viewerId ? state.count : 0;
}
